import { FormApi } from 'final-form';
import { times } from 'lodash';

import ClientTeamMemberSelect from 'components/Inputs/FormInputs/ClientTeamMemberSelect';
import { RoomConfiguration } from 'store/groupDetailsSlice';

import { FormValues } from './index';

interface RoomRendererProps {
  roomType: 'single' | 'double' | 'suite';
  roomIndex: number;
  config?: RoomConfiguration;
  form: FormApi<FormValues, Partial<FormValues>>;
  defaultGuests?: number;
}

const RoomRenderer = ({
  roomType,
  roomIndex,
  config,
  form,
  defaultGuests = 1,
}: RoomRendererProps) => {
  const guests = config?.guests ?? defaultGuests;
  const guestText = guests === 1 ? 'guest' : 'guests';
  const roomTypeCapitalized =
    roomType.charAt(0).toUpperCase() + roomType.slice(1);

  const renderGuestSelects = () => {
    if (roomType === 'double' && guests === 2) {
      // Traditional two-bed layout for double rooms with 2 guests
      return (
        <>
          <div>
            <label
              htmlFor={`${roomType}[${roomIndex}][firstBed]`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              First Bed
            </label>
            <ClientTeamMemberSelect
              name={`${roomType}[${roomIndex}][firstBed]`}
              testId={`${roomType}-room-select-${roomIndex}-first-bed`}
              onAdd={() => form.submit()}
              allowCreateGuest={true}
            />
          </div>
          <div>
            <label
              htmlFor={`${roomType}[${roomIndex}][secondBed]`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Second Bed{' '}
              <span className="text-xs text-gray-500">(Optional)</span>
            </label>
            <ClientTeamMemberSelect
              name={`${roomType}[${roomIndex}][secondBed]`}
              testId={`${roomType}-room-select-${roomIndex}-second-bed`}
              onAdd={() => form.submit()}
              allowCreateGuest={true}
            />
          </div>
        </>
      );
    }

    if (guests === 1) {
      // Single guest layout
      return (
        <ClientTeamMemberSelect
          name={`${roomType}[${roomIndex}]`}
          testId={`${roomType}-room-select-${roomIndex}`}
          onAdd={() => form.submit()}
          allowCreateGuest={true}
        />
      );
    }

    // Multiple guests layout (for single/suite rooms with multiple guests)
    return times(guests, (guestIndex) => (
      <ClientTeamMemberSelect
        key={`guest-${guestIndex}`}
        name={`${roomType}[${roomIndex}][guest${guestIndex}]`}
        testId={`${roomType}-room-select-${roomIndex}-guest-${guestIndex}`}
        onAdd={() => form.submit()}
        allowCreateGuest={true}
      />
    ));
  };

  return (
    <div
      key={`${roomType}-${roomIndex}`}
      className="mb-4 rounded-md border border-gray-200 bg-gray-50 p-4 hover:bg-primaryGray/10"
      data-testid={roomType === 'suite' ? 'suite-room-selections' : undefined}
    >
      <div className="mb-2 text-sm text-gray-700">
        <span className="font-semibold">
          Room {roomIndex + 1} ({roomTypeCapitalized} • {guests} {guestText})
        </span>
      </div>
      <div className="space-y-2">{renderGuestSelects()}</div>
    </div>
  );
};

export default RoomRenderer;
